import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Camera, X, Sparkles, AlertTriangle, Check, Loader2 } from "lucide-react";

// The three coach-facing states plus "not assessed". Keep in sync with
// VALID_SKILL_STATUSES on the backend (server.py:259).
const RATING_OPTIONS = [
  { value: "competent", label: "Competent", bg: "#CCFF00", fg: "#0A0A0A" },
  { value: "developing", label: "Developing", bg: "#FF4500", fg: "#0A0A0A" },
  { value: "not_yet", label: "Not Yet", bg: "#525252", fg: "#E5E5E5" },
  { value: "not_assessed", label: "Skip", bg: "#141414", fg: "#A3A3A3" },
];

const ALL_LEVELS = ["2.5", "3.0", "3.5", "4.0", "4.5"];

/**
 * ScanChecklistModal — coach snaps a photo of the ticked paper sheet, Claude
 * vision reads the ticks, coach reviews the pre-filled ratings, taps Save.
 *
 * Flow:
 *   1. Pick level(s) — defaults to player's current level, coach can toggle more
 *   2. Choose file (uses camera on mobile via `capture="environment"`)
 *   3. Backend returns per-skill ratings with an `unclear` flag
 *   4. Coach reviews; yellow-highlighted rows need explicit confirmation
 *   5. Save All → per-skill POST to /coach/players/{id}/assessment-skill
 */
export default function ScanChecklistModal({
  open,
  onClose,
  studentId,
  studentName = "",
  currentLevel = null,
  onSaved,
}) {
  const [levels, setLevels] = useState(new Set(currentLevel ? [currentLevel] : ["3.0"]));
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [skipped, setSkipped] = useState([]);
  const [edits, setEdits] = useState({}); // skill_id -> rating (coach's picks)
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Reset every time modal reopens — keeps state predictable
    setLevels(new Set(currentLevel ? [currentLevel] : ["3.0"]));
    setFile(null);
    setPreviewUrl("");
    setResults(null);
    setSkipped([]);
    setEdits({});
    setScanning(false);
    setSaving(false);
  }, [open, currentLevel]);

  useEffect(() => {
    // Revoke old blob URL when a new file replaces it — avoids memory leak
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  if (!open) return null;

  const toggleLevel = (lv) => {
    const next = new Set(levels);
    if (next.has(lv)) next.delete(lv); else next.add(lv);
    // Never allow an empty selection — silently keep the last one
    if (next.size === 0) return;
    setLevels(next);
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Photo is too large (max 8 MB). Please retake at lower resolution.");
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setResults(null);
    setSkipped([]);
    setEdits({});
  };

  const runScan = async () => {
    if (!file) { toast.error("Please choose a photo first."); return; }
    if (levels.size === 0) { toast.error("Pick at least one level to read."); return; }
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      fd.append("levels", ALL_LEVELS.filter((l) => levels.has(l)).join(","));
      const { data } = await api.post("/assessment-checklist/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResults(data.results || []);
      setSkipped(data.skipped || []);
      // Seed edits with Claude's ratings — coach can then override any row
      const seed = {};
      (data.results || []).forEach((r) => {
        // For unclear rows we leave the pick empty until the coach confirms —
        // safer than pre-selecting a low-confidence guess.
        seed[r.skill_id] = r.unclear ? "" : r.rating;
      });
      setEdits(seed);
      const unclearCount = (data.results || []).filter((r) => r.unclear).length;
      toast.success(
        `Read ${data.results?.length || 0} skills` +
        (unclearCount ? ` · ${unclearCount} need a look` : "") +
        (data.skipped?.length ? ` · ${data.skipped.length} skipped` : "")
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Scan failed — please retake and try again.");
    } finally {
      setScanning(false);
    }
  };

  const setEdit = (sid, val) => {
    setEdits((prev) => ({ ...prev, [sid]: val }));
  };

  const saveAll = async () => {
    if (!results?.length) return;
    // Only save rows the coach has explicitly rated (non-empty pick, not "not_assessed")
    const toSave = results
      .map((r) => ({ ...r, pick: edits[r.skill_id] }))
      .filter((r) => r.pick && r.pick !== "not_assessed");
    if (!toSave.length) {
      toast.error("Nothing to save yet — pick a rating on at least one row.");
      return;
    }
    setSaving(true);
    let ok = 0;
    let failed = 0;
    for (const row of toSave) {
      try {
        await api.post(`/coach/players/${studentId}/assessment-skill`, {
          skill_id: row.skill_id,
          status: row.pick,
          notes: row.unclear ? "Confirmed via photo scan (was unclear)" : "Rated via photo scan",
        });
        ok += 1;
      } catch (e) {
        failed += 1;
      }
    }
    setSaving(false);
    if (ok) toast.success(`Saved ${ok} skill${ok === 1 ? "" : "s"}${failed ? ` (${failed} failed)` : ""}`);
    else toast.error("Could not save any skills. Please try again.");
    if (onSaved) onSaved();
    if (!failed) onClose();
  };

  // Portal to <body> so ancestor `transform` (e.g. .fade-up) doesn't turn a
  // position:fixed backdrop into a scoped-inside-parent element.
  return createPortal((
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="scan-checklist-modal">
      <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626]">
          <div className="flex items-center gap-2">
            <Camera className="w-4 h-4 text-[#CCFF00]" />
            <div>
              <div className="text-sm font-medium tracking-wide">Scan printed sheet</div>
              {studentName && <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">for {studentName}</div>}
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#141414]" data-testid="scan-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Level picker + camera trigger — always visible so coach can rescan */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="label-mono text-[10px] mb-2">Which levels are on this sheet?</div>
              <div className="flex flex-wrap gap-1.5">
                {ALL_LEVELS.map((lv) => {
                  const on = levels.has(lv);
                  return (
                    <button
                      key={lv}
                      onClick={() => toggleLevel(lv)}
                      className={`text-xs px-3 py-1.5 border transition-colors ${
                        on ? "border-[#CCFF00] bg-[#CCFF00]/10 text-[#CCFF00]" : "border-[#262626] text-[#A3A3A3] hover:border-[#525252]"
                      }`}
                      data-testid={`scan-level-${lv}`}
                    >
                      {lv}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="label-mono text-[10px] mb-2">Photograph</div>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                onChange={onPickFile}
                className="hidden"
                data-testid="scan-file-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                className="w-full text-xs px-3 py-2 border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] text-left"
                data-testid="scan-choose-photo"
              >
                {file ? `Change photo (${(file.size / 1024).toFixed(0)} KB)` : "Choose photo / take picture"}
              </button>
            </div>
          </div>

          {/* Preview + Run button */}
          {previewUrl && (
            <div className="border border-[#262626] p-3">
              <div className="flex items-start gap-3">
                <img src={previewUrl} alt="preview" className="max-h-48 max-w-[45%] object-contain border border-[#262626]" data-testid="scan-preview-img" />
                <div className="flex-1">
                  <div className="text-xs text-[#E5E5E5] mb-1">Ready to scan.</div>
                  <div className="text-[10px] text-[#A3A3A3] leading-relaxed mb-3">
                    Claude will read the ticks and pre-fill the ratings. You'll review each row before saving —
                    faint or unclear ticks are highlighted for your confirmation.
                  </div>
                  <button
                    onClick={runScan}
                    disabled={scanning || saving}
                    className="inline-flex items-center gap-2 text-xs px-4 py-2 bg-[#CCFF00] text-[#0A0A0A] font-semibold disabled:opacity-50"
                    data-testid="scan-run-btn"
                  >
                    {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    {scanning ? "Reading sheet…" : results ? "Rescan" : "Read sheet"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Results table */}
          {results && results.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="label-mono text-[10px]">Review ratings ({results.length})</div>
                <div className="text-[10px] text-[#A3A3A3]">
                  {results.filter((r) => r.unclear).length} highlighted for your check
                </div>
              </div>
              <div className="border border-[#262626]" data-testid="scan-results-table">
                {results.map((r, idx) => {
                  const pick = edits[r.skill_id] ?? "";
                  const needsPick = r.unclear && !pick;
                  return (
                    <div
                      key={r.skill_id}
                      className={`grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#141414] last:border-b-0 items-center ${
                        needsPick ? "bg-[#FFD70011]" : idx % 2 ? "bg-[#0d0d0d]" : ""
                      }`}
                      data-testid={`scan-row-${r.skill_id}`}
                    >
                      <div className="col-span-5">
                        <div className="text-xs text-[#E5E5E5] leading-tight">{r.name}</div>
                        <div className="text-[10px] text-[#A3A3A3]">{r.group} · L{r.level}</div>
                      </div>
                      <div className="col-span-5 flex gap-1 justify-end">
                        {RATING_OPTIONS.map((opt) => {
                          const selected = pick === opt.value;
                          return (
                            <button
                              key={opt.value}
                              onClick={() => setEdit(r.skill_id, opt.value)}
                              className={`text-[10px] px-2 py-1 border transition-colors ${
                                selected
                                  ? "border-transparent font-semibold"
                                  : "border-[#262626] text-[#A3A3A3] hover:border-[#525252]"
                              }`}
                              style={selected ? { background: opt.bg, color: opt.fg } : undefined}
                              data-testid={`scan-pick-${r.skill_id}-${opt.value}`}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                      <div className="col-span-2 text-right">
                        {r.unclear ? (
                          <span className="inline-flex items-center gap-1 text-[10px] text-[#FFD700]">
                            <AlertTriangle className="w-3 h-3" /> Confirm
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] text-[#A3A3A3]">
                            <Check className="w-3 h-3" /> {(r.confidence * 100).toFixed(0)}%
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {skipped.length > 0 && (
                <div className="mt-2 text-[10px] text-[#A3A3A3]">
                  <span className="uppercase tracking-wider">Skipped by AI:</span> {skipped.length} skill{skipped.length === 1 ? "" : "s"} were not visible on the sheet — mark them manually if needed.
                </div>
              )}
            </div>
          )}

          {results && results.length === 0 && (
            <div className="border border-[#262626] p-4 text-xs text-[#A3A3A3]" data-testid="scan-no-results">
              Claude couldn't read any skills from that photo. Try a straight-on shot with better lighting.
            </div>
          )}
        </div>

        <div className="border-t border-[#262626] px-5 py-3 flex items-center justify-between">
          <div className="text-[10px] text-[#A3A3A3]">
            Reads printed sheet · powered by Claude vision · you decide what saves
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="text-xs px-4 py-2 border border-[#262626] hover:border-[#525252]"
              data-testid="scan-cancel"
            >
              Cancel
            </button>
            <button
              onClick={saveAll}
              disabled={!results?.length || saving || scanning}
              className="text-xs px-4 py-2 bg-[#CCFF00] text-[#0A0A0A] font-semibold disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="scan-save-all"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              {saving ? "Saving…" : `Save ${Object.values(edits).filter((v) => v && v !== "not_assessed").length || ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}
